import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, SecurityContext, ViewChild } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, first, map, Observable, of, Subject, take, takeUntil, debounceTime, Subscription } from 'rxjs';
import { LgvClientService, LgvTSHelper, LgvUserService, NotificationService } from '../../../../core/core.module';
import { LgvModel } from '../../../../data-models/longevity.model';
import { LgvEmailModel } from '../../../../data-models/lgv-email-model';
import { ActivatedRoute, Router } from '@angular/router';
import { StdObj } from '../../../../data-models/standard-objects';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LinkEmailToClientModalComponent } from '../../../../shared/link-email-to-client-modal/link-email-to-client-modal.component';
import { DocumentViewerModalComponent } from '../../../../shared/document-viewer-modal/document-viewer-modal.component';
import { MatSidenav } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment as env } from '../../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LgvSignalRService } from '../../../../core/services/lgv-signal-r.service';

@Component({
    selector: 'lgv-view-email',
    templateUrl: './view-email.component.html',
    styleUrls: ['./view-email.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ViewEmailComponent implements OnInit, OnDestroy {

    @ViewChild('agentSignatureElement') agentSignatureElement: ElementRef;
    @ViewChild('searchDiv') searchDiv: ElementRef;
    @ViewChild('searchDivParent') searchDivParent: ElementRef;
    @ViewChild('slideMenu') slideMenu: MatSidenav;

    pageMode$ = new BehaviorSubject(PageMode.Default);
    draftMode$ = new BehaviorSubject('');
    onDestroy$ = new Subject();

    _emailList: LgvEmailModel.Email[] = [];
    emailList$ = new BehaviorSubject(this._emailList);

    _selectedClients: LgvModel.ClientInfo[] = [];
    selectedClients$ = new BehaviorSubject(this._selectedClients);

    _paginationInfo: StdObj.PaginationInfo = null;
    paginationInfo$ = new BehaviorSubject(this._paginationInfo);

    _selectedEmailFromSearch: LgvEmailModel.Email = null;
    selectedEmailFromSearch$ = new BehaviorSubject(this._selectedEmailFromSearch);

    _selectedEmailFromList: LgvEmailModel.Email = null;
    selectedEmailFromList$ = new BehaviorSubject(this._selectedEmailFromList);

    _selectedEmailBody: string = null;
    selectedEmailBody$ = new BehaviorSubject(this._selectedEmailBody);

    _safeEmailBody: SafeHtml = null;
    safeEmailBody$ = new BehaviorSubject(this._safeEmailBody);

    searchText:string = '';
    dummySearchResults = ['Joe Walsh', 'Bruce Dickenson'];

    _clientSearchSummary: LgvModel.ClientSearchSummary = this.emptyClientSummary();
    clientSearchSummary$ = new BehaviorSubject(this._clientSearchSummary);

    _emailSearchSummary: LgvEmailModel.EmailSearchSummary = this.emptyEmailSummary();
    emailSearchSummary$ = new BehaviorSubject(this._emailSearchSummary);


    _folderList: LgvEmailModel.EmailFolder[] = [];
    folderList$ = new BehaviorSubject(this._folderList);

    _selectedFolder: LgvEmailModel.EmailFolder = null;
    selectedFolder$ = new BehaviorSubject(this._selectedFolder);

    // fileAttachments are local files to upload
    _fileAttachments: EmailAttachment[] = [];
    fileAttachments$ = new BehaviorSubject(this._fileAttachments);

    // savedAttachments are files that have already been uploaded and saved as draft
    _savedAttachments: LgvEmailModel.Attachment[] = [];
    savedAttachments$ = new BehaviorSubject(this._savedAttachments);

    showFolderPane$ = new BehaviorSubject(false);
    isLoading$ = new BehaviorSubject(true);
    isSending$ = new BehaviorSubject(false);
    isSaving$ = new BehaviorSubject(false);
    searchIsExpanded$ = new BehaviorSubject(false);
    isSearchingClients$ = new BehaviorSubject(false);
    isSearchingEmails$ = new BehaviorSubject(false);
    isFilterByUnread$ = new BehaviorSubject(false);
    isSortDescending$ = new BehaviorSubject(true);
    isFullScreenEmailView$ = new BehaviorSubject(false);
    emailWasTruncated$ = new BehaviorSubject(false);
    fullEmailText: string = null;

    showCC$ = new BehaviorSubject(false);

    emailDraft: LgvEmailModel.Email;
    // rawEmailToString: string = '';
    // rawEmailCCString: string = '';

    toList:LgvEmailModel.EmailContact[] = [];
    ccList:LgvEmailModel.EmailContact[] = [];
    defaultToEmailList$ = new BehaviorSubject([] as LgvEmailModel.EmailContact[]);
    defaultCCEmailList$ = new BehaviorSubject([] as LgvEmailModel.EmailContact[]);

    dbUserSS: LgvModel.AgentSS;
    userId: string;

    clientIdParam:string = null;
    composeParam:boolean = false;
    emailParam:string = null;

    emailThreadMarker:string = null;

    isMobile$ = new BehaviorSubject(false);

    bodyImages$ = new BehaviorSubject([]as BodyImage[]);

    readonly breakpoint$ = this.breakpointObserver
      .observe(['(min-width: 768px)'])
      .pipe( distinctUntilChanged() );
      

    _selectedEmailForViewingPane: LgvEmailModel.Email = null;
    selectedEmailForViewingPane$ = new BehaviorSubject(this._selectedEmailForViewingPane);

    get clientSearchResultsShortList$(): Observable<LgvModel.ClientInfo[]> {return this.clientSearchSummary$.pipe(first(f=> !!f),map(summary=> summary.value?.slice(0,5)))}
    get emailSearchResultsShortList$(): Observable<LgvEmailModel.Email[]> {return this.emailSearchSummary$.pipe(first(f=> !!f),map(summary=> summary.values?.slice(0,5)))}

    get showingFolderPaneInDesktopMode$(): Observable<boolean> { return of(!this.isMobile$.value && this.showFolderPane$.value)}

    get agentEmailAddress(): string {
        let environmentName = '';
        switch(env.envName){
            case 'DEV': environmentName = 'development-'; break;
            case 'STAGING': environmentName = 'staging-'; break;
            case 'UAT': environmentName = 'uat-'; break;
        }

        return `${this.dbUserSS?.agentInfo?.username}@${environmentName}mail.rfm.io`;
    }

    private autoSaveDebounceTime = 20000; // 20 seconds
    private autoSaveSubscription: Subscription;
    private lastEditTime = new Date();
    public lastSaveTime$ = new BehaviorSubject<Date>(null);
    private emailContentChanged$ = new Subject<void>();

    constructor(
        public LgvUserSvc: LgvUserService,
        private router: Router,
        private http: HttpClient,
        private route: ActivatedRoute,
        private lgvClientSvc: LgvClientService,
        private breakpointObserver: BreakpointObserver,
        public matDialog: MatDialog,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer,
        public notificationSvc: NotificationService,
        private signalRService: LgvSignalRService
    ) { }

    ngOnInit(): void {
        // Wait for user service to be ready before proceeding
        if (!this.LgvUserSvc) {
            this.notificationSvc.error('User service not initialized');
            return;
        }

        this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
            this.clientIdParam = params['clientID'];
            this.composeParam = params['compose'] ? true : false;
            this.emailParam = params['emailParam'];
        });

        this.breakpoint$.subscribe(() =>{
            this.isMobile$.next(this.breakpointObserver.isMatched('(max-width: 768px)'))
        });
          
        this.isMobile$.next(this.breakpointObserver.isMatched('(max-width: 768px)'));

        this.initPagination();
        this.selectedEmailFromList$.next(null);
        this.selectedEmailBody$.next(null);
        this.safeEmailBody$.next(null);
        this.initFolderPane();

        // default to inbox.
        this.selectedFolder$.next({ code:'Inbox', displayName: 'Inbox', unreadEmailCount: 0, isSelected: true, displayOrder:0 });

        // Subscribe to SignalR notifications and forward them to the user service
        this.signalRService.onNewEmail.pipe(takeUntil(this.onDestroy$)).subscribe(newEmail => {
            this.LgvUserSvc.handleNewEmail(newEmail);
        });

        // Subscribe to email service notifications
        this.LgvUserSvc.onNewEmail.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.refreshEmailList();
        });

        // Initialize user data and start fetching emails
        this.LgvUserSvc.DBUserSS$.pipe(
            filter(f => !!f),
            takeUntil(this.onDestroy$)
        ).subscribe(ss => {
            this.userId = ss.agentInfo.id;
            this.dbUserSS = ss;
            if (this.clientIdParam) {
                this.initFromClientId();
            }
            if(this.emailParam){
                this.composeDirectEmail();
            }
            else{
                this.fetchEmailData();
            }
        });

        this.renderer.listen('window', 'click', (e: Event) => {
            if (this.searchIsExpanded$.value === true) {
                if ( e.target !== this.searchDiv.nativeElement 
                    && e.target !== this.searchDivParent.nativeElement
                    && !this.searchDiv.nativeElement.contains(e.target)
                    && !this.searchDivParent.nativeElement.contains(e.target)) {

                    this.searchIsExpanded$.next(false);
                }
            }
        });

        this.LgvUserSvc.folderSummary$.pipe(
            filter(f => !!f),
            takeUntil(this.onDestroy$)
        ).subscribe(folderSummary => {
            let tmpFolders:LgvEmailModel.EmailFolder[] = [];

            for(let f of folderSummary.folders){
                if(f.code === this.selectedFolder$.value.code){
                    f.isSelected = true;
                }
                else{
                    f.isSelected = false;
                }
                tmpFolders.push(f);
            }

            this.folderList$.next(tmpFolders);

            let selectedFolder = tmpFolders.find(x=> x.code === this.selectedFolder$.value.code);
            this.selectedFolder$.next(selectedFolder);
        },
        (err: Error) => {
            const msg = 'There was an error fetching email summary';
            console.log(msg);
            this.notificationSvc.error(msg);
        });

        this.autoSaveSubscription = this.emailContentChanged$.pipe(
            debounceTime(this.autoSaveDebounceTime),
            filter(() => (this.lastEditTime.getTime() - new Date().getTime() <= this.autoSaveDebounceTime) && this.isReadyToSave)
        ).subscribe(() => {
            this.saveDraft(true);
            this.lastSaveTime$.next(new Date());
        });
    }

    ngOnDestroy() {
        if (this.fullEmailText)
            this.fullEmailText = null

        this.renderer.destroy();
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
        if (this.autoSaveSubscription) {
            this.autoSaveSubscription.unsubscribe();
        }
    }

    // Call this when email content changes (subject, body, recipients)
    onEmailContentChanged() {
        this.lastEditTime = new Date();
        this.emailContentChanged$.next();
    }

    composeDirectEmail(){
        // start a new email to email address
        this.fetchEmailData();
        this.onNewMail();
        this.defaultToEmailList$.next( [{ 
            id: null, 
            name:'', 
            email: this.emailParam
        }]);
    }

    initFromClientId(){
        //search contacts
        this.isLoading$.next(true);
        this.lgvClientSvc.FetchClientInfo(this.clientIdParam).pipe(take(1)).subscribe(client=>{

            if(this.composeParam && this.clientIdParam){
                // start a new email to client
                this.fetchEmailData();
                this.onNewMail();
                // this.rawEmailToString = client.email;
                this.defaultToEmailList$.next( [{ 
                    id: client.id, 
                    name:`${client.firstName} ${client.lastName}`, 
                    email: client.email
                }]);
            }
            else{
                // select client and filter emails
                this.selectedClients$.value.push(client);
                this.selectedClients$.next(this.selectedClients$.value);
                this.searchText = '';
                this.fetchEmailData();
            }

        });
        
    }

    // add a delay so search only executes after a pause
    timeoutId: NodeJS.Timeout = null;
    onSearchTextChange() {
        let _this = this;
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(function () {
            // console.log(`***Execute *** ${_this.timeoutId} - Search Text: ${_this.searchText}`);
            _this.executeTextSearch();
        }, 500);
    }
      
    executeTextSearch(){
        console.log(this.searchText);
        let searchLength = this.searchText.trim().length;

        if(searchLength < 3){
            this.clientSearchSummary$.next(this.emptyClientSummary());
            this.emailSearchSummary$.next(this.emptyEmailSummary());
        }

        if(this.searchText.trim().length >= 3){
            this.isSearchingClients$.next(true);
            this.isSearchingEmails$.next(true);

            //search contacts
            this.lgvClientSvc.clientSearch(this.searchText, this.dbUserSS.company.id, this.dbUserSS.organization.id, this.userId).pipe(take(1)).subscribe(clientSearchResult=>{
                console.log('contact search results:');
                console.log(clientSearchResult);
                this.clientSearchSummary$.next(clientSearchResult);

                console.log('clientSearchSummary:');
                console.log(this.clientSearchSummary$.value);
                this.isSearchingClients$.next(false);
            });

            // search emails
            this.LgvUserSvc.getUserEmails(this.userId, 
                this.selectedFolder$.value.code, 
                this.searchText
            ).pipe(take(1)).subscribe(emailSearchResult=>{
                console.log('email search results:');
                console.log(emailSearchResult);
                this.emailSearchSummary$.next(emailSearchResult);
                this.isSearchingEmails$.next(false);
            });

        }
    }

    fetchEmailData(){
        // this.genDummyData();
        this.isLoading$.next(true);

        // this.lgvUserSvc.getUserEmailSummary(this.userId).pipe(take(1)).subscribe(summary => {
        //     let tmpFolders:LgvEmailModel.EmailFolder[] = [];

        //     // let foundSelectedItem = false;
        //     for(let f of summary.folders){

        //         if(f.code === this.selectedFolder$.value.code){
        //             f.isSelected = true;
        //             // foundSelectedItem = true;
        //         }
        //         else{
        //             f.isSelected = false;
        //         }
        //          tmpFolders.push(f);
        //     }

        //     // // if selected email is not in the new batch, then unselect it
        //     // if(this.selectedEmailFromList$.value && !foundSelectedItem){
        //     //     this.selectedEmailFromList$.next(null);
        //     // }

        //     this.folderList$.next(tmpFolders);

        //     let selectedFolder = tmpFolders.find(x=> x.code === this.selectedFolder$.value.code);
        //     this.selectedFolder$.next(selectedFolder);
        // },
        //     (err: Error) => {
        //         const msg = 'There was an error fetching email summary';
        //         console.log(msg);
        //         this.notificationSvc.error(msg);
        //     }
        // );

        this.LgvUserSvc.getUserEmails(this.userId, 
            this.selectedFolder$.value.code, 
            this.searchText, 
            this.isFilterByUnread$.value,
            this.isSortDescending$.value ? 'receivedDateUtc desc' : 'receivedDateUtc',
            this.selectedClients$.value,
            this.paginationInfo$.value
        ).pipe(take(1)).subscribe(summary=> {

            if(this.selectedEmailFromList$.value){
                for(let e of summary.values){
                    e.isSelected = e.id === this.selectedEmailFromList$.value.id;
                }

                // check if selected email is still available
                if(summary.values.filter(x=> x.isSelected).length === 0 ){
                    this.selectedEmailFromList$.next(null);
                }
            }

            this.emailList$.next(summary.values);
            this.isLoading$.next(false);
            this.updatePaginationInfo(summary);

        },
        (err: Error) => {
            this.isLoading$.next(false);
            const msg = 'There was an error fetching emails for selected folder';
            console.log(msg);
            this.notificationSvc.error(msg);
        });
    }

    initFolderPane() {
        let val = localStorage.getItem('lgv.email.show-folder-pane');
        let bShow: boolean = (val && val === 'true');
        this.showFolderPane$.next(bShow);
    }

    setFolderPane(bShow: boolean) {
        this.showFolderPane$.next(bShow);
        localStorage.setItem('lgv.email.show-folder-pane', bShow.toString());
    }

    onToggleFolderPane() {
        if(this.isFullScreenEmailView$.value){
            this.onExitFullScreen();
        }

        this.setFolderPane(!this.showFolderPane$.value);
    }

    onExitFullScreen(){
        this.isFullScreenEmailView$.next(false);
    }

    onToggleFullScreenEmailView() {
        this.isFullScreenEmailView$.next(!this.isFullScreenEmailView$.value);

        if(this.selectedEmailFromSearch$.value){
            this.selectedEmailFromSearch$.next(null);
            this.searchText = '';
        }
    }

    onToggleCC() {
        this.showCC$.next(!this.showCC$.value);
    }

    onMouseOverEmail(email: LgvEmailModel.Email){
        // console.log('onMouseOverEmail()');
        // console.log(email);
    }

    onRefresh() {
        this.fetchEmailData();            
    }

    onNewMail() {

        if(this.isFullScreenEmailView$.value){
            this.onExitFullScreen();
        }

        this.emailThreadMarker = null;
        this.pageMode$.next(PageMode.ComposeEmail);
        this.emailDraft = this.newEmail();
        this.savedAttachments$.next([]);
        // this.setFocusToSendBtn();

        // this.selectedEmailFromList$.next(null);
    }

    onReply(email: LgvEmailModel.Email) {
        this.onNewMail();
        // Create a new email object instead of modifying the original
        let newEmail: LgvEmailModel.Email = {
            id: '',
            fromFull: { name: '', email: '' },
            toFull: [{ name: email.fromFull.name, email: email.fromFull.email }],
            subject: email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
            textBody: '',
            htmlBody: '',
            receivedDateUtc: '',
            isRead: true,
            isDeleted: false,
            isSelected: false,
            folder: 'Draft',
            userEmail: this.dbUserSS.agentInfo.email,
            userId: this.userId,
            clients: [...(email.clients || [])] // Copy any client associations
        };

        this.defaultToEmailList$.next([email.fromFull]);

        this.emailThreadMarker = `On ${LgvTSHelper.jsonDateToDateTimeString(email.receivedDateUtc)}, ${email.fromFull.name} &lt;${email.fromFull.email}&gt; wrote:`;
        newEmail.htmlBody = `<br><br><div>${this.emailThreadMarker}<br></div><br>
        <div>${this.selectedEmailBody$.value}</div>
        `;
        this.emailDraft = newEmail;
    }

    onReplyAll(email: LgvEmailModel.Email) {
        this.onNewMail();
        // Create a new email object instead of modifying the original
        let newEmail: LgvEmailModel.Email = {
            id: '',
            fromFull: { name: '', email: '' },
            toFull: [],
            subject: email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
            textBody: '',
            htmlBody: '',
            receivedDateUtc: '',
            isRead: true,
            isDeleted: false,
            isSelected: false,
            folder: 'Draft',
            userEmail: this.dbUserSS.agentInfo.email,
            userId: this.userId,
            clients: [...(email.clients || [])] // Copy any client associations
        };

        let toList = [...email.toFull];
        toList.push(email.fromFull);
        // remove the agent's email if present
        toList = toList.filter(x=> x.email.toUpperCase() !== this.agentEmailAddress.toUpperCase());
        this.defaultToEmailList$.next(toList);
        newEmail.toFull = toList;
        
        if(email.ccFull?.length > 0){
            this.defaultCCEmailList$.next([...email.ccFull]);
            newEmail.ccFull = [...email.ccFull];
        }

        this.emailThreadMarker = `On ${LgvTSHelper.jsonDateToDateTimeString(email.receivedDateUtc)}, ${email.fromFull.name} &lt;${email.fromFull.email}&gt; wrote:`;
        newEmail.htmlBody = `<br><br><div>${this.emailThreadMarker}<br></div><br>
        <div>${this.selectedEmailBody$.value}</div>
        `;
        this.emailDraft = newEmail;
    }

    emailContactsToString(contacts: LgvEmailModel.EmailContact[]): string {
        let bldString = '';
        
        let arrayLen = contacts.length;
        contacts.forEach((contact, index)=>{
            bldString += contact.email;
            if(index < arrayLen -1){
                bldString += ", "
            }
        });

        return bldString;
    }

    onForward(email: LgvEmailModel.Email) {
        this.onNewMail();
        let newEmail = structuredClone(email);
        newEmail.toFull = [];
        newEmail.subject = newEmail.subject.startsWith('Fwd: ') ? newEmail.subject : `Fwd: ${newEmail.subject}`;

        this.emailThreadMarker = `<p>---------- Forwarded message ---------</p>`;
        newEmail.htmlBody = `<br><br><div>${this.emailThreadMarker}<br>
        From: <strong>${email.fromFull.name}</strong> <span>&lt;${email.fromFull.email}&gt;
        </span><br>
        Date: ${LgvTSHelper.jsonDateToDateTimeString(email.receivedDateUtc)}<br>
        Subject: ${email.subject}<br>
        To: ${this.buildToListHTML(email)}<br></div><br><br>
        ${this.selectedEmailBody$.value}
        `
        this.emailDraft = newEmail;
    }


    buildToListHTML(email: LgvEmailModel.Email):string {
        let rtnVal = '';
        for(let contact of email.toFull){
            rtnVal += `&lt;<a href="${contact.email}">${contact.email}</a>&gt;`;
        }
        return rtnVal;
    }

    linkEmailToClient(email: LgvEmailModel.Email) {
        console.log('linkEmailToClient()');

        const dialogRef = this.matDialog.open(LinkEmailToClientModalComponent, {
            // width: '250px',
            data: { 
                selectedAgentId: this.userId, 
                selectedAgentOrganizationId: this.dbUserSS.organization.id, 
                selectedAgentCompanyId: this.dbUserSS.company.id
             }
        });

        dialogRef.afterClosed().pipe(first()).subscribe(x => {
            if (x?.action === true && x?.selectedClient) {

                let clientDTO = x.selectedClient as LgvModel.ClientDTO;

                if(email.clients?.find(x=> x.id === clientDTO.id)){
                    return;
                }

                email.clients.push({id:clientDTO.id, email: clientDTO.email, firstName: clientDTO.firstName, lastName: clientDTO.lastName});
                this.updateEmail(email, true, true);
            }
        });
    }

    onEmailClicked(email: LgvEmailModel.Email) {
        this.selectThisEmail(email);

        if(this.isMobile$.value && this.slideMenu?.opened){
            this.slideMenu.close();
        }

    }

    selectThisEmail(email: LgvEmailModel.Email, emailFromListView = true) {
        // kxk: todo - we may want to auto save drafts at some point, but not now.  Clear any attachments...
        this.fileAttachments$.next([]);
        this.savedAttachments$.next([]);
        let isLoadingDraft = this.selectedFolder$.value.code === 'Draft';

        if(this.isMobile$.value){
            this.pageMode$.next(PageMode.ViewEmailMobile);
        }

        if(!email.isRead){
            this.markEmailAsRead(email);
        }

        if (email.isSelected) {
            // Maya wants to ignore click if email is already selected
            return;
        }
        else {

            if(emailFromListView){
                if(isLoadingDraft){
                    this.pageMode$.next(PageMode.ComposeEmail);
                }
                else{
                    if(this.pageMode$.value === PageMode.ComposeEmail){
                        this.pageMode$.next(PageMode.Default);
                    }
                }
    
                this.selectedEmailFromList$.next(email);
                this.selectedEmailForViewingPane$.next(email);
                this.selectedEmailBody$.next(null);
                this.safeEmailBody$.next(null);

                if(!email.isRead){
                    // trigger folder refresh for new count
                    setTimeout(() => {
                        this.LgvUserSvc.getFolderSummary();
                    }, 2000);
                }
            }

            // Reset email viewing state
            if (this.fullEmailText) {
                this.fullEmailText = null;
            }
            this.emailWasTruncated$.next(false);
            this.selectedEmailBody$.next(null);
            this.safeEmailBody$.next(null);

            this.LgvUserSvc.getUserEmailDetails(this.userId, email.id).pipe(take(1)).subscribe(x => {
                if (x.folder === 'Draft') {
                    this.loadDraft(x);
                    return;
                }
                this.selectedEmailForViewingPane$.next(x);

                this.fullEmailText = this.processEmailBody(x.htmlBody);

                const formattedHtml = this.formatHtmlWithLineBreaks(this.fullEmailText);
        
                // Find reply marker and truncate if found
                const markerIndex = this.findReplyMarker(formattedHtml);
                
                let truncatedHtml = formattedHtml;
                //not truncating the email to be able to see most recent attachment
                //leaving it here in case we want to re implement this in the future
                //
                /* if (markerIndex !== -1) {
                    truncatedHtml = formattedHtml.substring(0, markerIndex);
                    this.emailWasTruncated$.next(true);
                } */

                this.selectedEmailBody$.next(truncatedHtml);
                this.safeEmailBody$.next(this.sanitizer.bypassSecurityTrustHtml(truncatedHtml));
            });

            this.deselectEmails(email);
        }
    }

    processEmailBody(body:string): string{
        let tmpBody = this.findImageTagsAndStartDownloading(body);
        tmpBody = this.addTargetBlankToAllAnchorTags(tmpBody);
        return tmpBody;
    }

    showOriginalEmail() {
        if (this.fullEmailText) {
            const subject = this.selectedEmailForViewingPane$.value?.subject || 'Email';
            const fullHtml = `
                <html>
                    <head>
                        <title>Original: ${subject}</title>
                    </head>
                    <body>
                        ${this.fullEmailText}
                    </body>
                </html>
            `;
            const blob = new Blob([fullHtml], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            // Clean up the old URL
            URL.revokeObjectURL(url);
        }
    }

    findImageTagsAndStartDownloading(body:string): string{
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = body;

        let count=0;
        let tmpBodyImages = [];
        let email = this.selectedEmailForViewingPane$.value;
        

        const images = tempDiv.querySelectorAll('img');
        images.forEach((img) => {

            let curSource = img.attributes['src']?.value as string;
            if(curSource && curSource.startsWith('cid:')){
                let targetContentId = curSource.substring(4);
                let targetAttachment = email.attachments.find(x=> x.contentID === targetContentId);
                if(targetAttachment){
                    let key = `lgv_img:${targetAttachment.id}`;
                    tmpBodyImages.push({id:targetAttachment.id, url:null, attachment:targetAttachment});
                    img.setAttribute('src', key);
                }
            }
        }, email);        

        this.bodyImages$.next(tmpBodyImages);
        this.downloadBodyImages();

        return tempDiv.innerHTML;
    }

    updateImgTagWithLocalBlobImgUrl(bodyImage:BodyImage){
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.selectedEmailBody$.value;

        let count=0;
        let tmpBodyImages = [];
        let email = this.selectedEmailForViewingPane$.value;
        let targetSource = `lgv_img:${bodyImage.id}`;

        const images = tempDiv.querySelectorAll('img');
        images.forEach((img) => {

            let curSource = img.attributes['src']?.value as string;
            if(curSource && curSource === targetSource){
                img.setAttribute('src', bodyImage.url);

            }
        }, email);        

        let newHtml = tempDiv.innerHTML;
        this.selectedEmailBody$.next(newHtml);
        this.safeEmailBody$.next(this.sanitizer.bypassSecurityTrustHtml(newHtml));
    }


    downloadBodyImages(){
        let bodyImages = this.bodyImages$.value;

        bodyImages.forEach(bodyImage=>{

            this.downloadEmailAttachmentForLocalUse(this.userId, this.selectedEmailForViewingPane$.value.id, bodyImage.id, bodyImage.attachment.contentType)
            .pipe(take(1))
            .subscribe(tmpAttachmentUrl=> {
                // console.log('xxx Have Attachment:');
                // console.log(tmpAttachmentUrl);
                bodyImage.url = tmpAttachmentUrl;
                this.updateImgTagWithLocalBlobImgUrl(bodyImage);

                // remove image from attachments.  Now that we have them in the body, remove them from the attachments bar.
                let email = this.selectedEmailForViewingPane$.value;
                let tmpAttachments = email.attachments;
                let remIndex = tmpAttachments.findIndex(x => x.id === bodyImage.id);
                if (remIndex > -1) {
                    tmpAttachments.splice(remIndex, 1);
                    email.attachments = tmpAttachments;
                    this.selectedEmailForViewingPane$.next(email);
                }

            });

        });
    }

    downloadEmailAttachmentForLocalUse(userId:string, emailId: string, emailAttachmentId:string, contentType: string ):Observable<string> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${userId}/emails/${emailId}/attachments/${emailAttachmentId}`;
        const headers = new HttpHeaders({ 'Content-type': contentType });
        return this.http.get<Blob>(bldUrl, { headers: headers, responseType: 'blob' as 'json' }).pipe(take(1), map(x=> {
            let file = new Blob([x], { type: contentType });
            let fileUrl = URL.createObjectURL(file);
            return fileUrl;
        }))
    }


    addTargetBlankToAllAnchorTags(body:string): string{
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = body;

        const links = tempDiv.querySelectorAll('a');
        links.forEach(link => {
            link.setAttribute('target', '_blank');
        });        

        return tempDiv.innerHTML;
    }
    
    deselectEmails(newSelectedEmail: LgvEmailModel.Email) {
        let emailList = this.emailList$.value;
        for (let e of emailList) {
            e.isSelected = !newSelectedEmail || e.id === newSelectedEmail.id;
        }
        this.emailList$.next(emailList);
    }

    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if(this.pageMode$.value !== PageMode.Default){
            return;
        }
        
        switch (event.key) {
            case 'ArrowUp': event.stopPropagation(); this.onPreviousOrNextEmail(true); break;
            case 'ArrowDown': event.stopPropagation(); this.onPreviousOrNextEmail(false); break;
        }
    }

    currentSelectionIndex(email: LgvEmailModel.Email):number{
        let pi = this.paginationInfo$.value;
        let pageOffset = pi.currentPageIndex * pi.pageSize;
        let emailList = this.emailList$.value;

        let i=0;
        for(let e of emailList) {
            if(e.id === email.id){
                return i + pageOffset;
            }
            i++;
        };

        return -1;
    }

    onPreviousOrNextEmail(previous: boolean = false) {
        // console.log(`onPreviousOrNextEmail() => previous: ${previous}`);

        let list = this.emailList$.value;
        let selectedEmail = this.selectedEmailFromList$.value;

        if (!selectedEmail) {
            return;
        }

        let i = 0;
        for (let email of list) {
            if (email.id === selectedEmail.id) {

                // console.log(`Selected index: ${i}  Length: ${list.length}`);

                if (previous && i > 0) {
                    this.selectThisEmail(list[i - 1]);
                }

                else if (!previous && (i + 1) < list.length) {
                    this.selectThisEmail(list[i + 1]);
                }

                return;
            }
            i++;
        }
    }

    onExitViewEmailMobile(){
        this.pageMode$.next(PageMode.Default);
    }

    onSelectFolder(newFolder: LgvEmailModel.EmailFolder) {
        this.pageMode$.next(PageMode.Default);
        this.selectedEmailFromList$.next(null);
        this.selectedFolder$.next(newFolder);

        for (let folder of this.folderList$.value) {
            folder.isSelected = folder.code === newFolder.code;
        }
        this.folderList$.next(this.folderList$.value);
        this.fetchEmailData();
        

        if(this.isMobile$.value && this.slideMenu?.opened){
            this.slideMenu.close();
        }


        // setTimeout(() => {
        //     this.updateNumUnreadEmails();
        // }, 250);
    }

    markEmailAsRead(email: LgvEmailModel.Email) {
        if(email.isRead){
            return;
        }

        email.isRead = true;
        this.updateEmail(email, false);
        this.selectedFolder$.value.unreadEmailCount--;
        this.selectedFolder$.next(this.selectedFolder$.value);
    }

    markEmailAsUnread(email: LgvEmailModel.Email) {
        if(!email.isRead){
            return;
        }

        email.isRead = false;
        this.updateEmail(email, false);
        this.selectedFolder$.value.unreadEmailCount++;
        this.selectedFolder$.next(this.selectedFolder$.value);
    }

    updateEmail(email: LgvEmailModel.Email, triggerRefresh: boolean, bImmediateRefresh: boolean = false) {
        this.LgvUserSvc.updateUserEmail(this.userId, email).pipe(take(1)).subscribe(x=>{
            if(triggerRefresh){

                if(bImmediateRefresh){
                    this.fetchEmailData();
                }else{
                    setTimeout(() => {
                        this.fetchEmailData();
                    }, 1000);
                }
        
            }
            // trigger folder refresh for new count
            setTimeout(() => {
                this.LgvUserSvc.getFolderSummary();
            }, 2000);
        },
        (err: Error) => {
            const msg = 'There was an error updating your email';
            console.log(msg);
            this.notificationSvc.error(msg);
        });
    }

    buildEmailFormData(htmlBody: string): FormData {
        let formData = new FormData();
        
        // If email was truncated, combine edited content with original email
        if (this.emailWasTruncated$.value && this.fullEmailText) {
            // Find the reply marker in the edited content
            const markerIndex = this.findReplyMarker(htmlBody);
            if (markerIndex !== -1) {
                // Combine edited content with full original email
                htmlBody = htmlBody.substring(0, markerIndex) + this.fullEmailText;
            }
        }

        // Add the draft ID if it exists (for updating existing drafts)
        if (this.emailDraft?.id) {
            formData.append('id', this.emailDraft.id);
        }

        // Continue with existing form data building...
        this.toList.forEach((contact, i) => {
            formData.append(`toFull[${i}].email`, contact.email);
            formData.append(`toFull[${i}].name`, contact.name);
        });

        this.ccList.forEach((contact, i) => {
            formData.append(`ccFull[${i}].email`, contact.email);
            formData.append(`ccFull[${i}].name`, contact.name);
        });

        let i = 0;
        let attachments = this.fileAttachments$.value;
        attachments.forEach((a) => {
            formData.append(`attachments[${i}].file`, a.file);
            formData.append(`attachments[${i}].name`, a.file.name);
            i++;
        }); 

        let savedAttachments = this.savedAttachments$.value;
        savedAttachments.forEach((a) => {
            formData.append(`attachments[${i}].id`, a.id);
            formData.append(`attachments[${i}].name`, a.name);
            i++;
        }); 

        formData.append('subject', this.emailDraft?.subject);
        formData.append('htmlBody', htmlBody);
        formData.append('isRead', 'true');

        return formData;
    }

    addSignatureToEmailBody(htmlBody: string){
        let signature = this.agentSignatureElement.nativeElement.innerHTML;
        let rtnBody = htmlBody;
        
        let injectIndex = this.emailThreadMarker ? rtnBody.indexOf(this.emailThreadMarker) : null;

        if( this.emailThreadMarker && injectIndex != -1){
            const firstPart = rtnBody.slice(0, injectIndex);
            const secondPart = rtnBody.slice(injectIndex);
            rtnBody =  firstPart + signature + secondPart;
        } else{
            rtnBody = rtnBody + signature;
        }

        return rtnBody;
    }

    onSendEmail(){
        let dbUser = this.LgvUserSvc.DBUserSS$.value;

        this.isSending$.next(true);
        let htmlBody = this.addSignatureToEmailBody(this.emailDraft.htmlBody);
        let formData = this.buildEmailFormData(htmlBody);

        this.LgvUserSvc.sendUserEmail(dbUser.agentInfo.id, formData).subscribe(emails=> {
            this.emailDraft = this.newEmail();
            this.fileAttachments$.next([]);
            this.pageMode$.next(PageMode.Default);
            this.isSending$.next(false);
            this.showCC$.next(false);
            this.notificationSvc.success('Email has been sent ');
        },
        (err: Error) => {
            const msg = 'There was an error sending your email';
            this.isSending$.next(false);
            console.log(msg);
            this.notificationSvc.error(msg);
        });

    }

    get isReadyToSave(): boolean {
        return ((this.toList?.length > 0 || 
                this.ccList?.length > 0) &&
                (this.emailDraft?.subject?.trim()?.length > 0) &&
                (this.emailDraft?.htmlBody?.trim()?.length > 0));
    }

    saveDraft(saveOnly:boolean = false) {
        // caller should set draftMode$ if they want to get update event
        if(this.draftMode$.value !== 'Saving') {
            this.draftMode$.next('Saving');
        }
        let dbUserId = this.LgvUserSvc.DBUserSS$.value.agentInfo.id;

        this.isSaving$.next(true);
        let formData = this.buildEmailFormData(this.emailDraft.htmlBody);

        this.LgvUserSvc.saveDraftEmail(dbUserId, formData).subscribe(emails=> {
            if(!saveOnly) {
                this.emailDraft = this.newEmail();
                this.pageMode$.next(PageMode.Default);
                this.showCC$.next(false);
                this.draftMode$.next('');
                // Don't modify the email list - drafts are saved separately
            }
            this.isSaving$.next(false);
            this.notificationSvc.success('Email draft has been saved');
        },
        (err: Error) => {
            const msg = 'There was an error saving your draft';
            this.isSaving$.next(false);
            this.draftMode$.next('Error');
            console.log(msg);
            this.notificationSvc.error(msg);
        });
    }

    ContactArrayToRawEmailString(emailContacts:LgvEmailModel.EmailContact[]): string{
        let rtnVal = '';

        emailContacts.forEach((contact) =>{
            rtnVal += `${contact.email}; `
        });
        return rtnVal;
    }

    // rawEmailStringListToContactArray(strInput: string): LgvEmailModel.EmailContact[]{

    //     if(!strInput){
    //         return [];
    //     }

    //     strInput = strInput.trim();

    //     // attempt to support either , or ; seperator
    //     let sep = strInput.includes(';') ? ';' : ',';

    //     // convert raw input string to a list of EmailContact objects
    //     let rtnList = [];
    //     let parts = strInput.split(sep);
    //     if (parts.length > 0) {
    //         parts.forEach((part) => {

    //             let emailPart = part.trim();
    //             if(emailPart.length > 3){
    //                 rtnList.push({name:'', email: part});
    //             }
    //         });
    //     }
    //     return rtnList;
    // }

    onConfirmDiscardDraft(client: LgvModel.ClientInfo, bSilence: boolean) {

        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: { Confirmation: `Are you sure you want to delete this email draft?` }
        });

        dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(x => {
            if (x.action === true) {
                this.pageMode$.next(PageMode.Default);
                this.emailDraft = this.newEmail();
                // this.rawEmailToString = '';
                // this.rawEmailCCString = '';
                this.toList = [];
                this.ccList = [];
            }
        });
    }

    markEmailAsSpam(spamEmail: LgvEmailModel.Email, isSpam:boolean) {
        // this.saveDraftIfNeeded();

        // let haveSelectedEmail = this.selectedEmailFromList$.value !== null;
        // let deletingSelectedEmail = haveSelectedEmail && spamEmail.id == this.selectedEmailFromList$.value.id;

        let tmpArray = this.emailList$.value;
        let remIndex = tmpArray.findIndex(x => x.id === spamEmail.id);

        if (remIndex > -1) {
            let remEmail = tmpArray[remIndex];

            if(remEmail){
                remEmail.folder = isSpam ? 'Junk' : 'Inbox';
                this.updateEmail(remEmail, true);
                tmpArray.splice(remIndex, 1);
                this.emailList$.next(tmpArray);
            }

        }

        let newSelectedIndex = remIndex;
        if(newSelectedIndex >= this.emailList$.value.length){
            newSelectedIndex--;
        }

        let newSelectedEmail = this.emailList$.value[newSelectedIndex];
        this.selectThisEmail(newSelectedEmail);
    }

    onConfirmDeleteEmail(delEmail: LgvEmailModel.Email){
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: { Confirmation: `Are you sure you want to delete this email?` }
        });

        dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(x => {
            if (x.action === true) {
                this.deleteEmail(delEmail);
            }
        });
    }
    
    deleteEmail(delEmail: LgvEmailModel.Email) {
        // this.saveDraftIfNeeded();

        let emailWasRead = delEmail.isRead;

        let haveSelectedEmail = this.selectedEmailFromList$.value !== null;
        let deletingSelectedEmail = haveSelectedEmail && delEmail.id == this.selectedEmailFromList$.value.id;

        let tmpArray = this.emailList$.value;
        let remIndex = tmpArray.findIndex(x => x.id === delEmail.id);

        if (remIndex > -1) {
            let remEmail = tmpArray[remIndex];

            if(remEmail){

                if(this.selectedFolder$.value.code === 'Deleted')
                {
                    remEmail.isDeleted = true;
                }
                else{
                    remEmail.folder = 'Deleted';
                }
                this.updateEmail(remEmail, true);
                tmpArray.splice(remIndex, 1);
                this.emailList$.next(tmpArray);
            }

        }

        let newSelectedIndex = remIndex;
        if(newSelectedIndex >= this.emailList$.value.length){
            newSelectedIndex--;
        }

        if(deletingSelectedEmail){
            let newSelectedEmail = this.emailList$.value[newSelectedIndex];
            this.selectThisEmail(newSelectedEmail);
        }

        if(!emailWasRead){
            this.selectedFolder$.value.unreadEmailCount--;
            this.selectedFolder$.next(this.selectedFolder$.value);
        }

    }

    moveEmailToInbox(email: LgvEmailModel.Email) {
        let haveSelectedEmail = this.selectedEmailFromList$.value !== null;
        let movingSelectedEmail = haveSelectedEmail && email.id == this.selectedEmailFromList$.value.id;

        let tmpArray = this.emailList$.value;
        let remIndex = tmpArray.findIndex(x => x.id === email.id);

        if (remIndex > -1) {
            let remEmail = tmpArray[remIndex];

            if(remEmail){

                remEmail.isDeleted = false;
                remEmail.folder = 'Inbox';
                this.updateEmail(remEmail, true);
                tmpArray.splice(remIndex, 1);
                this.emailList$.next(tmpArray);
            }
        }

        let newSelectedIndex = remIndex;
        if(newSelectedIndex >= this.emailList$.value.length){
            newSelectedIndex--;
        }

        if(movingSelectedEmail){
            let newSelectedEmail = this.emailList$.value[newSelectedIndex];
            this.selectThisEmail(newSelectedEmail);
        }
    }

    needToSaveDraft():boolean{
        return false;
    }

    onClickAgentProfileLink(){
        // this.saveDraftIfNeeded();
        this.router.navigate([`/#/admin/view-agent/${this.userId}/agent-profile`]);
    }

    onOpenClientInNewWindow(clientId:string){
        let url = `/#/admin/client-overview/${clientId}`;
        window.open(url, "_blank");
    }

    countNumUnreadEmails(): number {
        // just count active folder.  Eventually we will have to do this for all folders.  Not sure the final schema, so hold off...

        // let activeFolder = this.selectedFolder$.value.code;
        let numUnread = this.emailList$.value.filter(x => !(!!x.isRead)).length;
        return numUnread;
    }

    newEmail(): LgvEmailModel.Email{
        this.fileAttachments$.next([]);
        // this.rawEmailToString = '';
        // this.rawEmailCCString = '';
        this.toList = [];
        this.ccList = [];

        return {    
            id: '',
            fromFull:{ name: '', email:''},
            toFull:[{ name: '', email:''}],
            subject: '',
            textBody: '',
            htmlBody: '',
            // x_preview: '',
            receivedDateUtc: '',
            isRead: false,
            isDeleted: false,
            // x_hasAttachment: false,
            isSelected: false,
            folder:'',
            userEmail: this.dbUserSS.agentInfo.email,
            userId:this.userId
        }
    }

    onSearchAreaClicked(event:any){
        // console.log('onSearchAreaClicked()');
        // console.log(event);

        if(this.searchIsExpanded$.value === false){
            setTimeout(() => {
                this.searchIsExpanded$.next(true);
            }, 50);
        }
    }

    onClearSearch(){
        this.searchText = '';

        setTimeout(() => {
            this.searchIsExpanded$.next(false);
            this.clientSearchSummary$.next(this.emptyClientSummary());
            this.emailSearchSummary$.next(this.emptyEmailSummary());
            this.pageMode$.next(PageMode.Default);
            this.fetchEmailData();
        }, 50);
    }


    setFilterByUnread(bFilter: boolean) {
        this.isFilterByUnread$.next(bFilter);
        this.fetchEmailData();
    }
    
    setSortDescending(bSortByDescending: boolean) {
        this.isSortDescending$.next(bSortByDescending);
        this.fetchEmailData();
    }
    
    onResetFilterAndSearch(){
        this.isFilterByUnread$.next(false);
        this.isSortDescending$.next(true);
        this.fetchEmailData();
    }

    onSaveDraft() {
        if( this.toList.length === 0
            && this.emailDraft?.subject?.trim().length === 0 
            && this.emailDraft?.htmlBody?.trim().length === 0)
        {
            this.pageMode$.next(PageMode.Default);
            this.isSending$.next(false);
            this.showCC$.next(false);
            setTimeout(() => {
                this.fetchEmailData();
            }, 1000);

            this.notificationSvc.warn("The Email Draft was empty.  There was nothing to save");
        }else{

            this.draftMode$.next('saving');

            // wait till draft is saved
            this.draftMode$.pipe(take(1)).subscribe(() => {
                if (this.draftMode$.value !== 'Error') {
                    this.pageMode$.next(PageMode.Default);
                    this.isSending$.next(false);
                    this.showCC$.next(false);
                    setTimeout(() => {
                        this.fetchEmailData();
                    }, 1000);
                }
            });
            this.saveDraft();
        }
    }

    saveDraftIfNeeded(){
        // this.saveDraft();
    }

    onShowAllResultsForSearch(){
        this.pageMode$.next(PageMode.SearchResults);
        this.emailList$.next(this.emailSearchSummary$.value.values);

        setTimeout(() => {
            this.searchIsExpanded$.next(false);
        }, 50);

    }

    emptyEmailSummary():LgvEmailModel.EmailSearchSummary {
        return {count:0, values:[]};
    }

    emptyClientSummary():LgvModel.ClientSearchSummary {
        return {count:0, value:[]};
    }

    onClientSearchResultClicked(client: LgvModel.ClientInfo){
        if(this.selectedClients$.value.find(x=> x.id === client.id)){
            console.log('Client is already selected');
            return;
        }

        this.selectedClients$.value.push(client);
        this.selectedClients$.next(this.selectedClients$.value);

        this.searchText = '';
        this.fetchEmailData();

        setTimeout(() => {
            this.searchIsExpanded$.next(false);
        }, 50);

    }

    onEmailSearchResultClicked(email: LgvEmailModel.Email){

        /*
            if the email is already in the folder list, then just select it.  Otherwise,
            load it in full screeen mode.
        */

        if(this.emailList$.value.find(x=> x.id === email.id)){
            this.selectThisEmail(email, true);
        }else{
            this.selectedEmailFromSearch$.next(email);
            this.selectedEmailForViewingPane$.next(email);
            this.isFullScreenEmailView$.next(true);
            this.selectThisEmail(email, false);
        }

        setTimeout(() => {
            this.searchIsExpanded$.next(false);
        }, 50);

    }

    onUnselectClient(client: LgvModel.ClientInfo) {
        let tmpArray = this.selectedClients$.value;
        let remIndex = tmpArray.findIndex(x => x.id === client.id);
        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1);
            this.selectedClients$.next(tmpArray);
        }

        this.fetchEmailData();
    }

    untagThisClient(client: LgvModel.ClientInfo, email: LgvEmailModel.Email) {


        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: { Confirmation: `Removing this tag will unlink this message from the client's profile.` }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(x => {
            if (x.action == true) {
                let tmpArray = email.clients;
                let remIndex = tmpArray.findIndex(x => x.id === client.id);
                if (remIndex > -1) {
                    tmpArray.splice(remIndex, 1);
                    email.clients = tmpArray;
                    this.updateEmail(email, true, true);
                }
            }
        });
    }

    updatePaginationInfo(summary: LgvEmailModel.EmailSearchSummary){
        if(!summary){
            this.initPagination();
            return;
        }

        let pi = this.paginationInfo$.value;
        pi.total = summary.count;
        // pi.currentPageIndex = 0;
        pi.numPages = Math.ceil(summary.count / pi.pageSize);
        this.paginationInfo$.next(pi);
    }

    initPagination(){
        let pi = new StdObj.PaginationInfo();
        pi.pageSize = 25;
        pi.total = 0;
        pi.numPages = 0;
        pi.currentPageIndex = 0;
        this.paginationInfo$.next(pi);
    }

    onPreviousPage(){
        console.log('onPreviousPage()');
        let pi = this.paginationInfo$.value;
        if(pi && pi.havePreviousPage)
        {
            pi.currentPageIndex--;
            this.paginationInfo$.next(pi);
            this.fetchEmailData();
        }
    }

    onNextPage(){
        console.log('onNextPage()');
        let pi = this.paginationInfo$.value;
        if(pi && pi.haveNextPage){
            pi.currentPageIndex++;
            this.paginationInfo$.next(pi);
            this.fetchEmailData();
        }
    }

      fileChangeEvent(event: any): void {
          if (event?.target?.files?.length > 0) {
              const files = event.target.files;

              if (files?.length > 0 && files[0].size > 0) {
                  let file = files[0];
                  let sizeInMB = file.size / 1024 / 1024;

                  if (sizeInMB > 20) {
                      this.notificationSvc.error('The file you selected exceeds the max size of 20 mb');
                  } else {
                      let attachments = this.fileAttachments$.value;
                      attachments.push({ id: LgvTSHelper.generateNewGuid(), file: file });
                      this.fileAttachments$.next(attachments);
                  }
              }

          }
      }
    
    onOpenAttachment(email:LgvEmailModel.Email, attachment: LgvEmailModel.Attachment){

        const dialogRef = this.matDialog.open(DocumentViewerModalComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '95%',
            width: '95%',
            data: { 
                email: email,
                emailAttachment: attachment,
                userId: this.userId,
                agentDTO: LgvModel.agentSSToAgentDTO(this.dbUserSS),
                selectedCompanyId:this.dbUserSS.company.id,
                selectedOrganizationId:this.dbUserSS.organization.id,
                contentType: attachment.contentType,
                newWindow: false
            }
        });
    }

    removeThisAttachment(attachment:EmailAttachment){
        console.log('removeThisAttachment:');
        console.log(attachment);


        let tmpArray = this.fileAttachments$.value;
        let remIndex = tmpArray.findIndex(x => x.id === attachment.id);
        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1);
            this.fileAttachments$.next(tmpArray);
        }
    }

    removeSavedAttachmentFromDraft(attachment:LgvEmailModel.Attachment){
        let tmpArray = this.savedAttachments$.value;
        let remIndex = tmpArray.findIndex(x => x.id === attachment.id);
        if (remIndex > -1) {
            tmpArray.splice(remIndex, 1);
            this.savedAttachments$.next(tmpArray);
        }

        let email = this.emailDraft;
        email.attachments = this.savedAttachments$.value;
        this.saveDraft(true);
    }


    onToListUpdated(list:LgvEmailModel.EmailContact[]){
        console.log('toListUpdated:');
        console.log(list);
        this.toList = list;
    }

    onCCListUpdated(list:LgvEmailModel.EmailContact[]){
        console.log('CCListUpdated:');
        console.log(list);
        this.ccList = list;
    }

    emailContactDisplay(emailContacts: LgvEmailModel.EmailContact[]):SafeHtml{
        let bldStr = '';

        if(!emailContacts || emailContacts.length == 0){
            return '[null]';
        }

        let firstEC = emailContacts[0];

        if(firstEC?.name?.length > 0){
            bldStr += firstEC.name;
        } else{
            bldStr += firstEC.email;
        }

        if(emailContacts.length > 1){
            bldStr += ` <small>(+${emailContacts.length-1})</small>`;
        }

        return this.sanitizer.bypassSecurityTrustHtml(bldStr) ;
    }

    normalizeEmailHtml(html: string): string {
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Replace <p>, <div>, and <br> tags with newlines
        const paragraphs = tempDiv.getElementsByTagName('p');
        for (const p of Array.from(paragraphs)) {
            p.innerHTML = p.innerHTML + '\n';
        }
        
        const divs = tempDiv.getElementsByTagName('div');
        for (const div of Array.from(divs)) {
            div.innerHTML = div.innerHTML + '\n';
        }
        
        // Get text content with preserved line breaks
        return tempDiv.innerText;
    }

    readonly quoteHeadersRegex = [
      /^-*\s*(On\s.+\s.+\n?wrote:{0,1})\s{0,1}-*$/m, // On DATE, NAME <EMAIL> wrote:
      // /^-*\s*(Le\s.+\s.+\n?écrit\s?:{0,1})\s{0,1}-*$/m, // Le DATE, NAME <EMAIL> a écrit :
      /^-*\s*(El\s.+\s.+\n?escribió:{0,1})\s{0,1}-*$/m, // El DATE, NAME <EMAIL> escribió:
      // /^-*\s*(Il\s.+\s.+\n?scritto:{0,1})\s{0,1}-*$/m,  // Il DATE, NAME <EMAIL> ha scritto:
      // /^-*\s*(Em\s.+\s.+\n?escreveu:{0,1})\s{0,1}-*$/m,  // Em DATE, NAME <EMAIL> ha escreveu:
      // /^\s*(Am\s.+\s)\n?\n?schrieb.+\s?(\[|<).+(\]|>):$/m, // Am DATE schrieb NAME <EMAIL>:
      // /^\s*(Op\s[\s\S]+?\n?schreef[\s\S]+:)$/m, // Il DATE, schreef NAME <EMAIL>:
      // /^\s*((W\sdniu|Dnia)\s[\s\S]+?(pisze|napisał(\(a\))?):)$/mu, // W dniu DATE, NAME <EMAIL> pisze|napisał:
      // /^\s*(Den\s.+\s\n?skrev\s.+:)$/m, // Den DATE skrev NAME <EMAIL>:
      // /^\s*(pe\s.+\s.+\n?kirjoitti:)$/m, // pe DATE NAME <EMAIL> kirjoitti: 
      // /^\s*(Am\s.+\sum\s.+\s\n?schrieb\s.+:)$/m, // Am DATE um TIME schrieb NAME:
      // /^(在[\s\S]+写道：)$/m, // > 在 DATE, TIME, NAME 写道：
      // /^(20[0-9]{2}\..+\s작성:)$/m, // DATE TIME NAME 작성:
      // /^(20[0-9]{2}\/.+의메세지:)$/m, // DATE TIME NAME 메세지:
      // /^(.+\s<.+>\sschrieb:)$/m, // NAME <EMAIL> schrieb:
      /^(.+\son.*at.*wrote:)$/m, // NAME on DATE wrote:
      /^\s*(From\s?:.+\s?\n?\s*[\[|<].+[\]|>])/m, // "From: NAME <EMAIL>" OR "From : NAME <EMAIL>" OR "From : NAME<EMAIL>"(With support whitespace before start and before <)
      // /^\s*(Von\s?:.+\s?\n?\s*[\[|<].+[\]|>])/m, // "Von: NAME <EMAIL>" OR "Von : NAME <EMAIL>" OR "Von : NAME<EMAIL>"(With support whitespace before start and before <)
      /^\s*(De\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m, // "De: NAME <EMAIL>" OR "De : NAME <EMAIL>" OR "De : NAME<EMAIL>"  (With support whitespace before start and before <)
      // /^\s*(Van\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m, // "Van: NAME <EMAIL>" OR "Van : NAME <EMAIL>" OR "Van : NAME<EMAIL>"  (With support whitespace before start and before <)
      // /^\s*(Da\s?:.+\s?\n?\s*(\[|<).+(\]|>))/m, // "Da: NAME <EMAIL>" OR "Da : NAME <EMAIL>" OR "Da : NAME<EMAIL>"  (With support whitespace before start and before <)
      /^(20[0-9]{2})-([0-9]{2}).([0-9]{2}).([0-9]{2}):([0-9]{2})\n?(.*)>:$/m, // 20YY-MM-DD HH:II GMT+01:00 NAME <EMAIL>:
      // /^\s*([a-z]{3,4}\.\s[\s\S]+\sskrev\s[\s\S]+:)$/m, // DATE skrev NAME <EMAIL>:
      /^([0-9]{2}).([0-9]{2}).(20[0-9]{2})(.*)(([0-9]{2}).([0-9]{2}))(.*)\"( *)<(.*)>( *):$/m, // DD.MM.20YY HH:II NAME <EMAIL>
      /^[0-9]{2}:[0-9]{2}(.*)[0-9]{4}(.*)\"( *)<(.*)>( *):$/, // HH:II, DATE, NAME <EMAIL>:
      /^(.*)[0-9]{4}(.*)from(.*)<(.*)>:$/,
      /^-{1,12} ?(O|o)riginal (M|m)essage ?-{1,12}$/i,
      // /^-{1,12} ?(O|o)prindelig (B|b)esked ?-{1,12}$/i,
      // /^-{1,12} ?(M|m)essage d\'origine ?-{1,12}$/i
    ];

    formatHtmlWithLineBreaks(html: string): string {
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Add newlines after paragraphs and divs
        const paragraphs = tempDiv.getElementsByTagName('p');
        for (const p of Array.from(paragraphs)) {
            p.innerHTML = p.innerHTML + '\n';
        }
        
        const divs = tempDiv.getElementsByTagName('div');
        for (const div of Array.from(divs)) {
            div.innerHTML = div.innerHTML + '\n';
        }
        
        return tempDiv.innerHTML;
    }

    findReplyMarker(html: string): number {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const plainText = tempDiv.innerText;
        
        let index :number = null;

        for (const regex of this.quoteHeadersRegex) {
            const match = plainText.match(regex);
            if (match && match.index) {
                if (index == null || match.index < index) {
                    index = match.index;
                }
            }
        }
        return index ?? -1;
    }

    private getAllTextNodes(node: Node): Node[] {
        const textNodes: Node[] = [];
        const walker = document.createTreeWalker(
            node,
            NodeFilter.SHOW_TEXT,
            null
        );

        let currentNode: Node | null;
        while (currentNode = walker.nextNode()) {
            textNodes.push(currentNode);
        }
        
        return textNodes;
    }

    public static sendClientEmailInApp(clientId: string){
        // should only be called for LOs w/ mailboxes
        let url = `/#/admin/view-email/client/${clientId}/compose/true`;
        window.open(url, "_blank");
    }

    public static sendEmailInApp(emailAddress: string, hasMailbox){
        // if user has a mailbox, send email in app, otherwise, use a mailto link.
        let url = '';
        if(hasMailbox){
            url = `/#/admin/view-email/email/${emailAddress}/compose/true`;
        }else{
            url = `mailto:${emailAddress}`;
        }

        window.open(url, "_blank");
    }

    public static genAbsoluteEmailUrl(emailAddress:string){
        const curUrlParts = window.location.href.split('#');

        if (curUrlParts.length !== 2) {
          return;
        }

        let appEmailUrl = `${curUrlParts[0]}#/admin/view-email/email/${emailAddress}/compose/true`;
        return appEmailUrl;
    }

    loadDraft(draft: LgvEmailModel.Email) {

        this.emailDraft = draft;
        this.pageMode$.next(PageMode.ComposeEmail);
        
        // Update the email lists and trigger UI updates
        this.toList = (draft.toFull || []) as LgvEmailModel.EmailContact[];
        this.defaultToEmailList$.next(this.toList);
        
        this.ccList = (draft.ccFull || []) as LgvEmailModel.EmailContact[];
        this.defaultCCEmailList$.next(this.ccList);
        
        // Show CC field if they have recipients
        if (this.ccList.length > 0) {
            this.showCC$.next(true);
        } else{
            this.showCC$.next(false);
        }
        
        // Process the email body
        const processedHtml = this.processEmailBody(draft.htmlBody);
        
        // Find reply marker and handle truncation
        const markerIndex = this.findReplyMarker(processedHtml);
        
        if (markerIndex !== -1) {
            // Store the full email content
            this.fullEmailText = processedHtml.substring(markerIndex);
            
            // Set the truncated version for editing
            const truncatedHtml = processedHtml.substring(0, markerIndex);
            this.emailDraft.htmlBody = truncatedHtml;
            this.emailWasTruncated$.next(true);
        } else {
            this.emailDraft.htmlBody = processedHtml;
            this.emailWasTruncated$.next(false);
            this.fullEmailText = null;
        }
        
        // Handle attachments if any
        if (draft.attachments?.length) {
            this.savedAttachments$.next(draft.attachments);
        }
    }

    get isReadyToSend(): boolean {
        return ((this.toList?.length > 0) &&
                (this.emailDraft?.subject?.trim()?.length > 0) &&
                (this.emailDraft?.htmlBody?.trim()?.length > 0));
    }

    refreshEmailList() {
        this.fetchEmailData();
    }
}

export enum PageMode { 
    Default='Default', 
    ViewEmailMobile='ViewEmailMobile', 
    ComposeEmail='ComposeEmail',
    SearchResults='SearchResults'
}

export interface EmailAttachment { 
    id:string;
    file:any;
}

export interface BodyImage{
    id:string;
    url?:string;
    attachment: LgvEmailModel.Attachment;
}